// apps/admin/src/components/monitoring/UploadHealthTab.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { collection, query, orderBy, limit, getDocs, collectionGroup, where } from 'firebase/firestore';
import { db, functions } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import {
  Upload,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  AlertCircle,
  BarChart3,
  Eye,
  FileText,
  Shield,
} from 'lucide-react';
import { Toast, ToastType } from '../common/Toast';
import { UploadTraceViewer } from './UploadTraceViewer';

// ============================================
// TYPES (mirrors getUploadMetrics response)
// ============================================

type FailureReason =
  | 'timeout'
  | 'bad_format'
  | 'rate_limited'
  | 'circuit_breaker'
  | 'validation_failed'
  | 'processing_error'
  | 'bigquery_write_failed'
  | 'aggregation_failed'
  | 'broker_update_failed'
  | 'audit_log_failed'
  | 'storage_access_error'
  | 'firestore_error'
  | 'pubsub_error'
  | 'unknown';

interface UploadMetricsResponse {
  sloTarget: number;
  sloMet: boolean;

  currentHour: {
    date: string;
    hour: number;
    totalUploads: number;
    successful: number;
    failed: number;
    successRate: number;
  };

  last24Hours: {
    totalUploads: number;
    successful: number;
    failed: number;
    successRate: number;
    trend: 'improving' | 'degrading' | 'stable';
  };

  last7Days: {
    totalUploads: number;
    successful: number;
    failed: number;
    successRate: number;
    daysAboveSlo: number;
  };

  failurePatterns: Array<{
    reason: FailureReason;
    count: number;
    percentage: number;
    trend: 'increasing' | 'decreasing' | 'stable';
  }>;

  companyPerformance: Array<{
    companyId: string;
    totalUploads: number;
    successRate: number;
    failureReasons: Record<string, number>;
  }>;

  alerts: Array<{
    type: 'slo_breach' | 'pattern_detected' | 'degradation';
    severity: 'warning' | 'critical';
    message: string;
    hour?: string;
    context?: Record<string, unknown>;
  }>;

  generatedAt: string;
  dataPoints: number;
  environment: 'dev' | 'stage' | 'prod';
}

interface RecentUpload {
  id: string;
  uploadId: string;
  companyId: string;
  templateId?: string;
  status: 'success' | 'failed' | 'rate_limited' | 'blocked';
  failureReason?: string;
  failureMessage?: string;
  recordCount: number;
  fileSizeBytes: number;
  durationMs: number;
  timestamp: any;
  fileName?: string;
  runNumber?: number;
  periodMonth?: string;
}

interface ToastState {
  message: string;
  type: ToastType;
  visible: boolean;
}

// ============================================
// HELPERS
// ============================================

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

function formatFailureReason(reason: string): string {
  return reason
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getTopFailureReason(reasons: Record<string, number>): string {
  const entries = Object.entries(reasons);
  if (entries.length === 0) return 'N/A';
  entries.sort((a, b) => b[1] - a[1]);
  return formatFailureReason(entries[0][0]);
}

// ============================================
// COMPONENT
// ============================================

export const UploadHealthTab: React.FC = () => {
  const [metrics, setMetrics] = useState<UploadMetricsResponse | null>(null);
  const [recentUploads, setRecentUploads] = useState<RecentUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [toast, setToast] = useState<ToastState>({ message: '', type: 'info', visible: false });
  const [traceUploadId, setTraceUploadId] = useState<string | null>(null);
  const [expandedFailure, setExpandedFailure] = useState<string | null>(null);

  const showToast = (message: string, type: ToastType) => {
    setToast({ message, type, visible: true });
  };

  // Fetch metrics via callable function
  const fetchMetrics = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const getUploadMetricsFn = httpsCallable<{ days: number }, UploadMetricsResponse>(functions, 'getUploadMetrics');
      const result = await getUploadMetricsFn({ days: 7 });
      const data = result.data;
      setMetrics(data);
      setLastRefresh(new Date());

      if (isRefresh) {
        showToast('Metrics refreshed successfully', 'success');
      }
    } catch (err: any) {
      console.error('Failed to fetch upload metrics:', err);
      setError(err.message || 'Failed to load upload metrics');
      if (isRefresh) {
        showToast(`Failed to refresh metrics: ${err.message}`, 'error');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Fetch recent uploads from Firestore (collectionGroup for upload_records across companies)
  const fetchRecentUploads = useCallback(async () => {
    try {
      // upload_records is a top-level collection keyed by companyId subcollections
      // Structure: upload_records/{companyId}/{uploadId}
      // Use collectionGroup query to get across all companies
      const q = query(
        collectionGroup(db, 'upload_records') as any,
        orderBy('timestamp', 'desc'),
        limit(20)
      );

      // Fallback: Try top-level collection if collectionGroup fails
      let docs: RecentUpload[] = [];
      try {
        const snapshot = await getDocs(q);
        docs = snapshot.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            id: d.id,
            uploadId: (data.uploadId as string) || d.id,
            companyId: (data.companyId as string) || '',
            templateId: data.templateId as string | undefined,
            status: (data.status as RecentUpload['status']) || 'failed',
            failureReason: data.failureReason as string | undefined,
            failureMessage: data.failureMessage as string | undefined,
            recordCount: (data.recordCount as number) || 0,
            fileSizeBytes: (data.fileSizeBytes as number) || 0,
            durationMs: (data.durationMs as number) || 0,
            timestamp: data.timestamp,
            fileName: data.fileName as string | undefined,
            runNumber: data.runNumber as number | undefined,
            periodMonth: data.periodMonth as string | undefined,
          };
        });
      } catch {
        // collectionGroup might not be indexed yet; silently fall back to empty
        console.warn('collectionGroup query for upload_records not available, skipping recent uploads');
      }

      setRecentUploads(docs);
    } catch (err) {
      console.error('Failed to fetch recent uploads:', err);
      // Non-critical, don't show error for this
    }
  }, []);

  // Initial fetch and auto-refresh every 5 minutes
  useEffect(() => {
    fetchMetrics();
    fetchRecentUploads();

    const interval = setInterval(() => {
      fetchMetrics(true);
      fetchRecentUploads();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchMetrics, fetchRecentUploads]);

  // ============================================
  // LOADING STATE
  // ============================================

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin mx-auto mb-3" />
          <div className="text-slate-400">Loading upload health metrics...</div>
        </div>
      </div>
    );
  }

  // ============================================
  // ERROR STATE
  // ============================================

  if (error && !metrics) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-3">
          <XCircle className="w-6 h-6 text-red-400" />
          <h3 className="text-lg font-semibold text-red-300">Failed to Load Upload Metrics</h3>
        </div>
        <p className="text-sm text-red-300 mb-4">{error}</p>
        <button
          onClick={() => fetchMetrics()}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!metrics) return null;

  // ============================================
  // SLO STATUS HELPERS
  // ============================================

  const sloColor =
    metrics.last7Days.successRate >= 99
      ? 'from-emerald-600 to-teal-600'
      : metrics.last7Days.successRate >= 95
        ? 'from-yellow-600 to-amber-600'
        : 'from-red-600 to-rose-600';

  const sloStatusLabel =
    metrics.last7Days.successRate >= 99
      ? 'SLO Met'
      : metrics.last7Days.successRate >= 95
        ? 'SLO At Risk'
        : 'SLO Breach';

  const sloStatusIcon =
    metrics.last7Days.successRate >= 99 ? (
      <Shield className="w-8 h-8 text-white" />
    ) : metrics.last7Days.successRate >= 95 ? (
      <AlertTriangle className="w-8 h-8 text-white" />
    ) : (
      <AlertCircle className="w-8 h-8 text-white" />
    );

  const trendIcon =
    metrics.last24Hours.trend === 'improving' ? (
      <TrendingUp className="w-5 h-5 text-emerald-400" />
    ) : metrics.last24Hours.trend === 'degrading' ? (
      <TrendingDown className="w-5 h-5 text-red-400" />
    ) : (
      <Minus className="w-5 h-5 text-slate-400" />
    );

  const trendLabel =
    metrics.last24Hours.trend === 'improving'
      ? 'Improving'
      : metrics.last24Hours.trend === 'degrading'
        ? 'Degrading'
        : 'Stable';

  // ============================================
  // RENDER
  // ============================================

  return (
    <>
      {toast.visible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, visible: false })}
        />
      )}

      {/* Trace Viewer Modal */}
      {traceUploadId && (
        <UploadTraceViewer
          uploadId={traceUploadId}
          onClose={() => setTraceUploadId(null)}
        />
      )}

      <div className="space-y-6">
        {/* Header with refresh */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-400">
            Last updated: {lastRefresh.toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney' })}
            {metrics.environment && (
              <span className="ml-2 px-2 py-0.5 bg-slate-700 rounded text-xs uppercase">
                {metrics.environment}
              </span>
            )}
          </div>
          <button
            onClick={() => fetchMetrics(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* ============================================ */}
        {/* SECTION 1: SLO STATUS BANNER */}
        {/* ============================================ */}
        <div className={`relative overflow-hidden rounded-xl bg-gradient-to-r ${sloColor} p-6 shadow-2xl`}>
          <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-10 rounded-full transform translate-x-20 -translate-y-20"></div>
          <div className="relative flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                {sloStatusIcon}
                <span className="text-white text-lg font-semibold">{sloStatusLabel}</span>
              </div>
              <p className="text-5xl font-bold text-white mb-1">
                {metrics.last7Days.successRate.toFixed(1)}%
              </p>
              <p className="text-white opacity-80 text-sm">
                7-day success rate vs {metrics.sloTarget}% SLO target
              </p>
            </div>
            <div className="text-right text-white">
              <div className="space-y-2">
                <div>
                  <p className="text-sm opacity-70">Total Uploads (7d)</p>
                  <p className="text-2xl font-bold">{metrics.last7Days.totalUploads.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm opacity-70">Days Above SLO</p>
                  <p className="text-2xl font-bold">{metrics.last7Days.daysAboveSlo}/7</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* SECTION 2: LIVE METRICS CARDS */}
        {/* ============================================ */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Current Hour: Total */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Current Hour</p>
                <p className="text-2xl font-bold text-white">{metrics.currentHour.totalUploads}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {metrics.currentHour.successful} ok / {metrics.currentHour.failed} failed
                </p>
              </div>
              <Upload className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          {/* Last 24 Hours: Success Rate + Trend */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">24h Success Rate</p>
                <p className="text-2xl font-bold text-white">
                  {metrics.last24Hours.successRate.toFixed(1)}%
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {trendIcon}
                  <span className={`text-xs ${
                    metrics.last24Hours.trend === 'improving'
                      ? 'text-emerald-400'
                      : metrics.last24Hours.trend === 'degrading'
                        ? 'text-red-400'
                        : 'text-slate-400'
                  }`}>
                    {trendLabel}
                  </span>
                </div>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-500" />
            </div>
          </div>

          {/* Last 24 Hours: Total Uploads */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">24h Uploads</p>
                <p className="text-2xl font-bold text-white">
                  {metrics.last24Hours.totalUploads}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {metrics.last24Hours.successful} ok / {metrics.last24Hours.failed} failed
                </p>
              </div>
              <FileText className="w-8 h-8 text-cyan-500" />
            </div>
          </div>

          {/* Active Alerts */}
          <div className={`bg-slate-800/50 backdrop-blur-sm border rounded-xl p-4 ${
            metrics.alerts.length > 0 ? 'border-red-500/50' : 'border-slate-700/50'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Active Alerts</p>
                <p className={`text-2xl font-bold ${
                  metrics.alerts.length > 0 ? 'text-red-400' : 'text-emerald-400'
                }`}>
                  {metrics.alerts.length}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {metrics.alerts.filter((a) => a.severity === 'critical').length} critical
                </p>
              </div>
              <AlertCircle className={`w-8 h-8 ${
                metrics.alerts.length > 0 ? 'text-red-500' : 'text-emerald-500'
              }`} />
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* ACTIVE ALERTS (if any) */}
        {/* ============================================ */}
        {metrics.alerts.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Active Alerts ({metrics.alerts.length})
              </h2>
            </div>
            <div className="divide-y divide-slate-700">
              {metrics.alerts.map((alert, idx) => (
                <div
                  key={idx}
                  className={`p-4 border-l-4 ${
                    alert.severity === 'critical'
                      ? 'border-red-500/50 bg-red-500/10'
                      : 'border-yellow-500/50 bg-yellow-500/10'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {alert.severity === 'critical' ? (
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-slate-500 uppercase px-2 py-0.5 bg-slate-700 rounded">
                          {alert.severity}
                        </span>
                        <span className="text-xs text-slate-500 uppercase px-2 py-0.5 bg-slate-700 rounded">
                          {alert.type.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300">{alert.message}</p>
                      {alert.hour && (
                        <p className="text-xs text-slate-500 mt-1">Hour: {alert.hour}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* SECTION 3: FAILURE PATTERNS TABLE */}
        {/* ============================================ */}
        {metrics.failurePatterns.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                Failure Patterns (7-day)
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/30">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Failure Reason</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-400">Count</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-400">Percentage</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400">Trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {metrics.failurePatterns.map((pattern) => (
                    <tr key={pattern.reason} className="hover:bg-slate-700/20">
                      <td className="px-4 py-3 text-sm text-white font-medium">
                        {formatFailureReason(pattern.reason)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <span className={`font-mono ${
                          pattern.count >= 5 ? 'text-red-400' : pattern.count >= 3 ? 'text-yellow-400' : 'text-slate-300'
                        }`}>
                          {pattern.count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-slate-300">
                        {pattern.percentage.toFixed(1)}%
                      </td>
                      <td className="px-4 py-3 text-center">
                        {pattern.trend === 'increasing' ? (
                          <span className="inline-flex items-center gap-1 text-xs text-red-400">
                            <TrendingUp className="w-3 h-3" /> Up
                          </span>
                        ) : pattern.trend === 'decreasing' ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                            <TrendingDown className="w-3 h-3" /> Down
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                            <Minus className="w-3 h-3" /> Stable
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* SECTION 4: COMPANY PERFORMANCE TABLE */}
        {/* ============================================ */}
        {metrics.companyPerformance.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-500" />
                Company Performance (7-day)
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/30">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Company</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-400">Total Uploads</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-400">Success Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Top Failure</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {[...metrics.companyPerformance]
                    .sort((a, b) => a.successRate - b.successRate)
                    .map((company) => (
                      <tr key={company.companyId} className="hover:bg-slate-700/20">
                        <td className="px-4 py-3 text-sm text-white font-mono">
                          {company.companyId.length > 30
                            ? company.companyId.substring(0, 30) + '...'
                            : company.companyId}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-slate-300 font-mono">
                          {company.totalUploads}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <span className={`font-mono font-bold ${
                            company.successRate >= 99
                              ? 'text-emerald-400'
                              : company.successRate >= 95
                                ? 'text-yellow-400'
                                : 'text-red-400'
                          }`}>
                            {company.successRate.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400">
                          {getTopFailureReason(company.failureReasons)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* SECTION 5: RECENT UPLOAD FEED */}
        {/* ============================================ */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-500" />
              Recent Uploads
            </h2>
          </div>

          {recentUploads.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-300 font-medium">No recent upload records found</p>
              <p className="text-sm text-slate-500 mt-1">
                Upload records will appear here once the metrics collection is active
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700 max-h-[600px] overflow-y-auto">
              {recentUploads.map((upload) => (
                <div key={upload.id}>
                  <div
                    className="p-4 hover:bg-slate-700/20 transition-colors cursor-pointer"
                    onClick={() =>
                      expandedFailure === upload.id
                        ? setExpandedFailure(null)
                        : upload.status === 'failed'
                          ? setExpandedFailure(upload.id)
                          : null
                    }
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Status badge */}
                        {upload.status === 'success' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-emerald-500/20 text-emerald-300">
                            <CheckCircle className="w-3 h-3" />
                            Success
                          </span>
                        ) : upload.status === 'failed' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-300">
                            <XCircle className="w-3 h-3" />
                            Failed
                          </span>
                        ) : upload.status === 'rate_limited' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-300">
                            <AlertTriangle className="w-3 h-3" />
                            Rate Limited
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-slate-500/20 text-slate-300">
                            <AlertCircle className="w-3 h-3" />
                            Blocked
                          </span>
                        )}

                        <div>
                          <p className="text-sm text-white font-medium">
                            {upload.companyId.length > 25
                              ? upload.companyId.substring(0, 25) + '...'
                              : upload.companyId}
                          </p>
                          <p className="text-xs text-slate-500">
                            {upload.fileName || upload.uploadId}
                            {upload.templateId && (
                              <span className="ml-2 text-slate-600">
                                [{upload.templateId.substring(0, 8)}...]
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-right">
                        <div>
                          <p className="text-xs text-slate-400">{formatDuration(upload.durationMs)}</p>
                          <p className="text-xs text-slate-500">{upload.recordCount} records</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">{formatBytes(upload.fileSizeBytes)}</p>
                          {upload.runNumber && (
                            <p className="text-xs text-slate-500">Run {upload.runNumber}</p>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTraceUploadId(upload.uploadId || upload.id);
                          }}
                          className="p-1.5 rounded-lg hover:bg-slate-600/50 transition-colors text-slate-400 hover:text-white"
                          title="View Trace"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expandable failure details */}
                  {expandedFailure === upload.id && upload.status === 'failed' && (
                    <div className="px-4 pb-4">
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                        <p className="text-xs text-red-300 font-mono">
                          <strong>Reason:</strong> {upload.failureReason ? formatFailureReason(upload.failureReason) : 'Unknown'}
                        </p>
                        {upload.failureMessage && (
                          <p className="text-xs text-red-300 mt-1 font-mono break-words">
                            <strong>Message:</strong> {upload.failureMessage}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Note */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <p className="text-sm text-blue-300">
            <strong>Upload Health Monitoring:</strong> This dashboard tracks the upload pipeline SLO ({metrics.sloTarget}% success rate target).
            Metrics are aggregated hourly from Firestore upload_metrics collection. Auto-refreshes every 5 minutes.
            Data points: {metrics.dataPoints} hours analyzed.
          </p>
        </div>
      </div>
    </>
  );
};
